import { PostHogIntegration } from '@src/monitoring/utils/PostHogIntegration';
import { formatUploadToArtifact } from '@src/utility/general';
import { v4 as uuid } from 'uuid';

import type { BRollAsset, BRollStore } from '@src/stores-v2/BRollStore';
import type { FileData } from '@src/types.ts/story';

export default class BRollService {
  private _bRollStore: BRollStore;

  constructor(bRollStore: BRollStore) {
    this._bRollStore = bRollStore;
  }

  get store() {
    return this._bRollStore;
  }

  async initialize(params: { storyId?: string; organizationId?: string }) {
    this.store.logger.info('Initializing BRollService', params);
    await this.store.initialize(params);
  }

  addPendingAssets(files: File[]) {
    this.store.logger.info(`Adding ${files.length} pending assets`);
    const assets: BRollAsset[] = files.map(file => ({
      id: uuid(),
      type: file.type.includes('image') ? 'photo' : 'video',
      status: 'pending',
      description: '',
      url: URL.createObjectURL(file),
      createdAt: new Date().toISOString(),
      file,
    }));
    assets.forEach(asset => {
      this.store.addPendingAsset(asset);
    });
    this.store.logger.debug('Added pending assets', {
      assetIds: assets.map(a => a.id),
    });
  }

  async saveArtifactsToEntity() {
    this.store.logger.info('Saving artifacts to entity');
    if (this.store.isStory && this.store.story) {
      this.store.logger.debug('Saving to story', {
        storyId: this.store.story.id,
      });
      await this.store.storyRepository.update(this.store.story);
    } else if (this.store.isOrganization && this.store.organization) {
      this.store.logger.debug('Saving to organization', {
        orgId: this.store.organization.id,
      });
      await this.store.albumRepository.update(this.store.organization);
    }
  }

  async uploadPendingAssets() {
    const pendingAssets = this.store.bRollAssets.filter(
      a => a.status === 'pending',
    );
    this.store.logger.info(`Uploading ${pendingAssets.length} pending assets`);
    await Promise.allSettled(
      pendingAssets.map(async asset => this.uploadPendingAsset(asset)),
    );
    await this.saveArtifactsToEntity();
  }

  async uploadAsset(asset: BRollAsset) {
    try {
      this.store.setAssetStatus(asset.id, 'uploading');
      const upload = await this.uploadFile(asset.file!, {
        id: asset.id,
        description: asset.description,
      });
      this.store.logger.debug('Asset upload successful', { assetId: asset.id });
      this.store.removePendingAsset(asset.id);
      this.store.appendArtifact(
        formatUploadToArtifact(upload, {
          title: asset.description,
        }),
      );
      this.store.setAssetStatus(asset.id, 'uploaded');
      return upload;
    } catch (error) {
      this.store.logger.error(`Failed to upload asset: ${asset.id}`, error);
      this.store.setAssetStatus(asset.id, 'failed');
    }
  }

  async uploadPendingAsset(asset: BRollAsset) {
    if (asset.status !== 'pending') return;
    this.store.logger.info(`Uploading asset: ${asset.id}`, {
      type: asset.type,
    });
    await this.uploadAsset(asset);
  }

  async retryFailedAsset(asset: BRollAsset) {
    if (asset.status !== 'failed') return;
    this.store.logger.info(`Retrying failed upload for asset: ${asset.id}`, {
      type: asset.type,
    });
    await this.uploadAsset(asset);
    await this.saveArtifactsToEntity();
  }

  async uploadFile(
    file: File,
    params: {
      id: string;
      description: string;
    },
  ) {
    this.store.logger.info(`Uploading file: ${file.name}`, {
      assetId: params.id,
    });
    const fileName = file.name.split('.')[0];
    const currentIdentity =
      this.store.contributor || this.store.userIdentityStore.currentIdentity;
    const newFileData: FileData & { fileName: string } = {
      type: 'artifact',
      file,
      fileName,
      alt: '',
      title: params.description,
      author: currentIdentity?.name,
      tags:
        currentIdentity?.email && currentIdentity?.name
          ? [currentIdentity.email, currentIdentity.name]
          : undefined,
      customData: {
        "Contributor's Name": currentIdentity?.name,
        "Contributor's Email": currentIdentity?.email,
      },
    };
    const newUpload = await this.store.assetRepository.uploadFile(newFileData);
    this.store.logger.debug('File upload completed', {
      assetId: params.id,
      uploadId: newUpload.id,
    });
    return newUpload;
  }

  async removeAsset(assetId: string) {
    this.store.logger.info(`Removing asset: ${assetId}`);
    const asset = this.store.bRollAssets.find(a => a.id === assetId);
    if (!asset) {
      this.store.logger.warn(`Asset not found: ${assetId}`);
      return;
    }
    if (asset.status === 'pending') {
      this.store.logger.debug('Removing pending asset', { assetId });
      this.store.removePendingAsset(assetId);
    } else {
      this.store.logger.debug('Removing uploaded asset', {
        assetId,
        isStory: this.store.isStory,
        isOrg: this.store.isOrganization,
      });
      if (this.store.isStory && this.store.story) {
        this.store.setStoryArtifacts(
          this.store.story.storyArtifacts?.filter(a => a.id !== assetId),
        );
        this.store.setStoryArtifactsVideo(
          this.store.story.storyArtifactsVideo?.filter(a => a.id !== assetId),
        );
      } else if (this.store.isOrganization && this.store.organization) {
        this.store.setOrganizationArtifacts(
          this.store.organization.organizationArtifacts?.filter(
            a => a.id !== assetId,
          ),
        );
        this.store.setOrganizationArtifactsVideo(
          this.store.organization.organizationArtifactsVideo?.filter(
            a => a.id !== assetId,
          ),
        );
        this.store.setOrganizationAudios(
          this.store.organization.organizationAudios?.filter(
            a => a.id !== assetId,
          ),
        );
      }
      await this.saveArtifactsToEntity();
    }
  }

  async updateAssetDescription(asset: BRollAsset, description: string) {
    this.store.logger.info(`Updating asset description: ${asset.id}`, {
      oldDescription: asset.description,
      newDescription: description,
    });
    asset.description = description;
    if (asset.status === 'uploaded') {
      if (this.store.isStory && this.store.story) {
        this.store.logger.debug('Updating story artifact description', {
          assetId: asset.id,
        });
        await this.store.storyRepository.update({
          id: this.store.story.id,
          storyArtifacts: this.store.story.storyArtifacts?.map(a =>
            a.id === asset.id ? { ...a, title: description } : a,
          ),
          storyArtifactsVideo: this.store.story.storyArtifactsVideo?.map(a =>
            a.id === asset.id ? { ...a, title: description } : a,
          ),
        });
      } else if (this.store.isOrganization && this.store.organization) {
        this.store.logger.debug('Updating organization artifact description', {
          assetId: asset.id,
        });
        await this.store.albumRepository.update({
          id: this.store.organization.id,
          organizationArtifacts:
            this.store.organization.organizationArtifacts?.map(a =>
              a.id === asset.id ? { ...a, title: description } : a,
            ),
          organizationArtifactsVideo:
            this.store.organization.organizationArtifactsVideo?.map(a =>
              a.id === asset.id ? { ...a, title: description } : a,
            ),
        });
      }
    }
  }

  saveContributor(contributor: Record<'name' | 'email', string>) {
    this.store.logger.info('Saving contributor', contributor);
    PostHogIntegration.identify(contributor.email, {
      email: contributor.email,
      name: contributor.name,
      type: 'contributor',
      source: 'broll',
    });
    this.store.setContributor(contributor);
  }
}
